import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Loader2, Pencil, Plus, Star, Trash2, Layers } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';

/**
 * Named Gift Generator setups — the picker that sits above the whole Gift
 * Generator config. Switching here loads that setup's complete package
 * (gifts, inventory, wallpaper, scratch logo, sponsor step) for editing.
 */
export const GiftSetupPicker = ({
    setups = [], valueId, onSelect, onCreate, onDuplicate, onRename,
    onSetDefault, onDelete, busy = false,
}) => {
    const { t } = useTranslation();
    const [dialog, setDialog] = useState(null);   // 'create' | 'rename'
    const [name, setName] = useState('');
    const current = setups.find((s) => s.id === valueId);

    const open = (mode) => {
        setName(mode === 'rename' ? (current?.name || '') : '');
        setDialog(mode);
    };
    const submit = async () => {
        const clean = name.trim();
        if (!clean) return;
        const ok = await (dialog === 'rename' ? onRename(clean) : onCreate(clean));
        if (ok !== false) setDialog(null);
    };

    return (
        <div className="rounded-lg border border-fuchsia-500/25 bg-fuchsia-500/[0.04] px-3 py-3 space-y-2"
             data-testid="gg-setup-picker">
            <div className="flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-fuchsia-500 shrink-0" />
                <Label className="text-sm font-medium">{t('giftSetups.pickerTitle')}</Label>
                {current?.is_default && (
                    <Badge variant="outline" className="h-5 text-[10px] border-fuchsia-500/40 text-fuchsia-500"
                           data-testid="gg-setup-default-badge">
                        {t('giftSetups.orgDefault')}
                    </Badge>
                )}
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
            </div>
            <Select value={valueId || ''} onValueChange={onSelect} disabled={busy}>
                <SelectTrigger className="h-9" data-testid="gg-setup-select">
                    <SelectValue placeholder={t('giftSetups.pickerPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                    {setups.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`gg-setup-option-${s.id}`}>
                            {s.name}{s.is_default ? ` — ${t('giftSetups.orgDefault')}` : ''}
                            {` (${t('giftSetups.giftCount', { count: s.gift_count })})`}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <div className="flex flex-wrap gap-1.5">
                <Button type="button" variant="outline" size="sm" disabled={busy}
                        onClick={() => open('create')} data-testid="gg-setup-create-btn">
                    <Plus className="w-3 h-3 mr-1" />{t('giftSetups.createNew')}
                </Button>
                <Button type="button" variant="outline" size="sm" disabled={busy || !current}
                        onClick={() => open('rename')} data-testid="gg-setup-rename-btn">
                    <Pencil className="w-3 h-3 mr-1" />{t('giftSetups.rename')}
                </Button>
                <Button type="button" variant="outline" size="sm" disabled={busy || !current}
                        onClick={onDuplicate} data-testid="gg-setup-duplicate-btn">
                    <Copy className="w-3 h-3 mr-1" />{t('giftSetups.duplicate')}
                </Button>
                {current && !current.is_default && (
                    <>
                        <Button type="button" variant="outline" size="sm" disabled={busy}
                                onClick={onSetDefault} data-testid="gg-setup-set-default-btn">
                            <Star className="w-3 h-3 mr-1" />{t('giftSetups.setAsDefault')}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" disabled={busy}
                                onClick={onDelete} data-testid="gg-setup-delete-btn"
                                className="text-muted-foreground hover:text-red-500">
                            <Trash2 className="w-3 h-3 mr-1" />{t('giftSetups.delete')}
                        </Button>
                    </>
                )}
            </div>
            <p className="text-[11px] text-muted-foreground">{t('giftSetups.pickerHint')}</p>

            <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
                <DialogContent data-testid="gg-setup-name-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {dialog === 'rename' ? t('giftSetups.renameTitle') : t('giftSetups.createTitle')}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label className="text-sm">{t('giftSetups.nameLabel')}</Label>
                        <Input
                            value={name}
                            maxLength={60}
                            autoFocus
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                            placeholder={t('giftSetups.namePlaceholder')}
                            data-testid="gg-setup-name-input"
                        />
                        <p className="text-[11px] text-muted-foreground">{t('giftSetups.nameHint')}</p>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDialog(null)}
                                data-testid="gg-setup-name-cancel">
                            {t('common.cancel')}
                        </Button>
                        <Button type="button" onClick={submit} disabled={!name.trim() || busy}
                                data-testid="gg-setup-name-save">
                            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                            {dialog === 'rename' ? t('giftSetups.rename') : t('giftSetups.createNew')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
